import React from "react";
import { useParams } from "react-router-dom";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import { useGetProductByIdQuery } from "src/services/ProductService";
const renderValue = (value: any) => {
  if (!value) return "-";

  // Array of links (e.g., product.image, demoLinks, courierCharges)
  if (Array.isArray(value)) {
    return (
      <div className="flex flex-wrap gap-2">
        {value.map((v, idx) =>
          typeof v === "string" && v.startsWith("http") ? (
            <a
              key={idx}
              href={v}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline break-all"
            >
              {v}
            </a>
          ) : (
            <span key={idx}>{v}</span>
          )
        )}
      </div>
    );
  }

  // Single string link
  if (typeof value === "string" && value.startsWith("http")) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 underline break-all"
      >
        {value}
      </a>
    );
  }

  return value;
};

const ProductViewLayout = () => {
  const { productId } = useParams();
  const { data, isLoading } = useGetProductByIdQuery(productId);

  if (isLoading) {
    return (
      <SideNavLayout>
        <div className="flex items-center justify-center h-screen">
          <p className="text-gray-500 text-lg">Loading...</p>
        </div>
      </SideNavLayout>
    );
  }

  if (!data) {
    return (
      <SideNavLayout>
        <div className="flex items-center justify-center h-screen">
          <p className="text-gray-500 text-lg">No product found</p>
        </div>
      </SideNavLayout>
    );
  }

  const product = data?.data;

  return (
    <SideNavLayout>
      <div className="flex flex-col lg:flex-row gap-4 p-4">
        {/* Left Panel */}
        <div className="w-full lg:w-1/3 flex-shrink-0 lg:sticky lg:top-4 lg:h-[calc(100vh-2rem)]">
          <div className="bg-white shadow rounded-lg p-4 flex flex-col gap-4 border border-gray-200">
            {/* Product Image */}
            <div className="w-full h-60 sm:h-72 md:h-80 rounded-md flex items-center justify-center bg-gray-50 border border-dashed overflow-hidden">
              {product?.image ? (
                <img
                  src={product.image[0]}
                  alt={product.productName}
                  className="h-full object-contain max-w-full"
                />
              ) : (
                <span className="text-gray-400 italic">No Image</span>
              )}
            </div>

            <h2 className="text-2xl font-semibold text-gray-900">
              {product?.productName}
            </h2>

            <div className="grid grid-cols-1 gap-3 text-sm text-gray-700">
              {[
                ["Course Name", product?.courseName],
                ["Level Name", product?.levelName],
                [
                  "Subject",
                  Array.isArray(product?.subject) && product.subject.length > 0
                    ? product.subject.map((s: any) => s.subjectName).join(", ")
                    : "-",
                ],
                [
                  "Faculty",
                  Array.isArray(product?.faculty) && product.faculty.length > 0
                    ? product.faculty.map((f: any) => f.facultyName).join(", ")
                    : "-",
                ],
              ].map(([label, value]) => (
                <div key={label} className="flex flex-col gap-1">
                  <span className="font-medium text-gray-600">{label}</span>
                  <div className="border rounded-md p-2 bg-white shadow-sm">
                    <span className="text-gray-800 break-words">
                      {value || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="w-full lg:w-2/3 flex flex-col gap-6 overflow-auto max-h-[calc(100vh-2rem)]">
          {/* Basic Details */}
          <div className="bg-gray-50 shadow-sm rounded-lg p-4">
            <h3 className="text-slate-800 font-semibold mb-3 text-lg">
              Basic Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
              {[
                ["Product Name", product?.productName],
                ["Code", product?.code],
                ["Variant Code", product?.varientCode],
                ["Nick Name", product?.nickname],
                ["Module Name", product?.modeName],
                ["Group Name", product?.groupName],
                ["Syllabus Name", product?.syllabusName],
                ["Mode Name", product?.modeName],
                ["Category Name", product?.categoryName],
                ["Product Slug", product?.productUrlSlug],
                ["Rank", product?.rank],
              ].map(([label, value]) => (
                <div key={label} className="flex flex-col gap-1">
                  <span className="font-medium text-gray-600">{label}</span>
                  <div className="border rounded-md p-2 bg-white shadow-sm">
                    <span className="text-gray-800 break-words">
                      {value || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* General Details */}
          <div className="bg-gray-50 shadow-sm rounded-lg p-4">
            <h3 className="text-slate-800 font-semibold mb-3 text-lg">
              General Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
              {[
                ["Product MRP", product?.productMrp],
                ["Max Discount", product?.maxDiscount],
                ["Max Discount Amount", product?.maxDiscountAmount],
                ["Referral to Percentage", product?.referralToPercentage],
                [
                  "Referral to Discount Amount",
                  product?.referralToDiscountAmount,
                ],
                ["Referral by Percentage", product?.referralByPercentage],
                [
                  "Referral by Discount Amount",
                  product?.referralByDiscountAmount,
                ],
                ["Wallet Discount", product?.walletDiscount],
                ["Wallet Discount Amount", product?.walletDiscountAmount],
                [
                  "Allowed Payment Gateway",
                  Array.isArray(product?.allowedPaymentGateway) &&
                  product.allowedPaymentGateway.length > 0
                    ? product.allowedPaymentGateway.join(", ")
                    : "-",
                ],
                [
                  "Courier Charges",
                  Array.isArray(product?.courierCharges) &&
                  product?.courierCharges.length > 0
                    ? product.courierCharges.join(", ")
                    : "-",
                ],
                ["Description", product?.impDescription],
              ].map(([label, value]) => (
                <div key={label} className="flex flex-col gap-1">
                  <span className="font-medium text-gray-600">{label}</span>
                  <div className="border rounded-md p-2 bg-white shadow-sm">
                    <span className="text-gray-800 break-words">
                      {value || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* More Details */}
          {/* More Details */}
          <div className="bg-gray-50 shadow-sm rounded-lg p-4">
            <h3 className="text-slate-800 font-semibold mb-3 text-lg">
              More Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
              {[
                ["Attempts", product?.attempts],
                ["Batch Recording", product?.batchRecording],
                ["Kit Content", product?.kitContent],
                ["Lecture Recording Date", product?.lectureRecordingDate],
                ["Video Runs On", product?.videoRunsOn],
                ["Search Key Code", product?.searchKeyCode],
                ["No OfViews", product?.noOfViews],
                ["View Count As", product?.viewCountAs],
                ["View Extension", product?.viewExtension],
                ["Validity", product?.validity],
                ["Validity Starts From", product?.validityStartsFrom],
                ["Validity Extension", product?.validityExtension],
                ["Topics Covered", product?.topicsCovered],
                ["Multiple Demo", product?.multipleDemo],
                ["Processing Time", product?.processingTime],
                ["Product Notes", product?.productNotes],
                ["Additional Offer", product?.additionalOffer],
                ["Language", product?.language],
                ["No Of Lectures", product?.noOfLectures],
                ["Duration", product?.duration],
                ["studyMaterial", product?.studyMaterial],
                ["GD Link", product?.gdLink],
                ["Demo Links", product?.demoLinks],
                ["Demo Name", product?.demoName],
                ["View Demo", product?.viewDemo],
                ["Contact Technical Support", product?.contactTechnicalSupport],
                ["Additional Requirement", product?.additionalRequirement],
                ["Details Amendment", product?.detailsAmendments],
                ["Why to Buy", product?.whyToBuy],
                ["Contact Tracking Support", product?.contactTrackingSupport],
                ["Shipping Method", product?.shippingMethod],
                ["Meta", product?.meta],
                ["Meta Title", product?.metaTitle],
                ["Img Alt Text", product?.imageAltText],
                ["Invoice Institute Name", product?.invoiceInstituteName],
                ["Rating", product?.rating],
                [
                  "Document to be provided for Order Processing",
                  product?.documentToBeProvidedForOrderProcessing,
                ],
                ["Order Google Sheet Link", product?.orderGoogleSheetLink],
                [
                  "Validity Explanation Video URL",
                  product?.validityExplanationVidUrl,
                ],
                [
                  "Product Explanation Video URL",
                  product?.productExplanationVidUrl,
                ],
                ["View Explanation Video URL", product?.viewExplanationVidUrl],
                ["Index", product?.index],
                ["Tiny URL", product?.tinyUrl],
                ["Additional Description", product?.additionalDescription],
                ["Unboxing Video Links", product?.unboxingVidLinks],
                [
                  "Image",
                  Array.isArray(product?.image) && product.image.length > 0 ? (
                    <div className="flex gap-2 flex-wrap">
                      {product.image.map((imgUrl: string, idx: number) => (
                        <div
                          key={idx}
                          className="flex flex-col items-start gap-1"
                        >
                          <img
                            src={imgUrl}
                            alt={`product-img-${idx}`}
                            className="h-16 w-16 object-cover rounded border"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    "-"
                  ),
                ],
                ["Remark", product?.remark],
              ].map(([label, value]) => (
                <div key={label} className="flex flex-col gap-1">
                  <span className="font-medium text-gray-600">{label}</span>
                  <div className="border rounded-md p-2 bg-white shadow-sm">
                    <span className="text-gray-800 break-words">
                      {renderValue(value)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SideNavLayout>
  );
};

export default ProductViewLayout;
